import React, {useState, useMemo, useEffect} from 'react';
import PropTypes from 'prop-types';
import './CommonTable.css';
import {Skeleton} from "@mui/material";

const CommonTable = ({
                         data = [],
                         columns,
                         itemsPerPage = 10,
                         defaultSortField = '',
                         defaultSortDirection = 'asc',
                         loading = false
                     }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState(defaultSortField);
    const [sortDirection, setSortDirection] = useState(defaultSortDirection);
    //Reset page pagination where data changes
    useEffect(() => {
        setCurrentPage(1);
    }, [data])
    //Skeleton loading
    const skeletonRows = useMemo(() => Array.from({ length: itemsPerPage }, (_, i) => i), [itemsPerPage]);
    
    // Sort data
    const sortedData = useMemo(() => {
        if (!sortField) return data;

        return [...data].sort((a, b) => {
            const aValue = a[sortField];
            const bValue = b[sortField];

            if (aValue === bValue) return 0;

            const comparison = aValue > bValue ? 1 : -1;
            return sortDirection === 'asc' ? comparison : -comparison;
        });
    }, [data, sortField, sortDirection]);

    // Paginate data
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedData.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedData, currentPage, itemsPerPage]);

    // Calculate total pages
    const totalPages = Math.ceil(data.length / itemsPerPage);

    // Handle sort
    const handleSort = (field) => {
        if (field === sortField) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    return (
        <div className="common-table-container">
            <table className="common-table">
                <thead>
                <tr>
                    {columns.map((column) => (
                        <th
                            key={column.field}
                            onClick={() => column.sortable !== false && handleSort(column.field)}
                            className={column.sortable !== false ? 'sortable' : ''}
                        >
                            {column.header}
                            {sortField === column.field && (
                                <span className="sort-indicator">
                    {sortDirection === 'asc' ? ' ▲' : ' ▼'}
                  </span>
                            )}
                        </th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {loading ? (
                    skeletonRows.map((rowIndex) => (
                        <tr key={`skeleton-${rowIndex}`} className="skeleton-row">
                            {columns.map((column) => (
                                <td key={`skeleton-${rowIndex}-${column.field}`}>
                                    <Skeleton />
                                </td>
                            ))}
                        </tr>
                    ))
                ) :
                paginatedData.length > 0 ? (
                    paginatedData.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {columns.map((column) => (
                                <td key={`${rowIndex}-${column.field}`}>
                                    {column.render ? column.render(row) : row[column.field]}
                                </td>
                            ))}
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan={columns.length} className="no-data">
                            No data available
                        </td>
                    </tr>
                )}
                </tbody>
            </table>

            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                    >
                        &laquo;
                    </button>

                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        &lsaquo;
                    </button>

                    <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        &rsaquo;
                    </button>

                    <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                    >
                        &raquo;
                    </button>
                </div>
            )}
        </div>
    );
};

CommonTable.propTypes = {
    data: PropTypes.array.isRequired,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            header: PropTypes.string.isRequired,
            render: PropTypes.func,
            sortable: PropTypes.bool,
        })
    ).isRequired,
    itemsPerPage: PropTypes.number,
    defaultSortField: PropTypes.string,
    defaultSortDirection: PropTypes.oneOf(['asc', 'desc']),
    loading: PropTypes.bool,
};

export default CommonTable;